// /web/src/components/ProtectedRoute.jsx
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './ProtectedRoute.css';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, isLoading, user, token, userEmail } = useContext(AuthContext);
  const location = useLocation();
  const [initialLoad, setInitialLoad] = useState(true);

  // Solo logs en desarrollo y solo en casos importantes
  const isDev = import.meta.env.DEV;

  // Memoizar el estado de autenticación para evitar re-renders innecesarios
  const authStatus = useMemo(() => ({
    isAuthenticated,
    isAdmin,
    isLoading,
    hasToken: !!token,
    userEmail,
    currentPath: location.pathname
  }), [isAuthenticated, isAdmin, isLoading, token, userEmail, location.pathname]);

  useEffect(() => {
    // Dar tiempo para que AuthContext se inicialice
    const timer = setTimeout(() => {
      setInitialLoad(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Log solo para debug crítico en desarrollo
  /*
  if (isDev && (isLoading || initialLoad)) {
    console.log('🛡️ ProtectedRoute - Inicializando para:', location.pathname);
  }
    */

  // Mostrar loading durante la inicialización
  if (isLoading || initialLoad) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  // Verificar autenticación
  if (!authStatus.isAuthenticated) {
    if (isDev) {
      console.log('❌ Usuario no autenticado, redirigiendo a login desde:', location.pathname);
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar permisos de administrador si es requerido
  if (requireAdmin && !authStatus.isAdmin) {
    if (isDev) {
      console.log('❌ Permisos de admin requeridos para:', location.pathname);
    }
    return (
      <div className="access-denied-container">
        <div className="access-denied-card">
          <h1>🚫 Acceso Denegado</h1>
          <p>No tienes permisos de administrador para acceder a esta página.</p>
          <p>Usuario actual: <strong>{authStatus.userEmail}</strong></p>
          <p>Rol: <strong>{authStatus.isAdmin ? 'Administrador' : 'Usuario'}</strong></p>
          <button 
            onClick={() => window.history.back()} 
            className="btn-secondary"
          >
            ← Volver
          </button>
        </div>
      </div>
    );
  }

  // Log para verificar optimización de re-renders
  if (isDev && (requireAdmin || location.pathname.includes('/admin'))) {
    console.log('✅ Acceso autorizado para:', location.pathname, 'Admin:', authStatus.isAdmin);
  }

  return children;
};

export default ProtectedRoute;